/**
 * Optimistic pending-bubble store.
 *
 * The architecture (Section 6) requires that as soon as the user signs in
 * their wallet, the message appears in the thread with a spinner — even
 * though the on-chain confirmation + indexer lag means we won't see the
 * real row for another 15-60s. The pending row is what makes the inbox
 * feel like a chat app rather than a CRUD form.
 *
 * State model:
 *   - keyed by `pending_id` returned from `/compose/prepare`
 *   - statuses: `signing` (TonConnect modal open), `pending` (signed + waiting
 *     for SSE pending_resolved), `failed` (user rejected; visible briefly
 *     before removal), `confirmed` (resolved — we keep the bubble visible
 *     until the real ThreadMessage shows up in the cache, then drop it).
 *   - `counterparty_raw` is canonical so we can match across thread cache keys.
 *
 * Lives outside react-query because it's purely client state with a custom
 * subscribe model. Components consume via the `usePendingThread` hook.
 */
import { useSyncExternalStore } from "react";
import type { ThreadMessage } from "../types/api";

export type PendingStatus = "signing" | "pending" | "confirmed" | "failed";

export interface PendingMessage {
  pending_id: string;
  counterparty_raw: string;
  comment: string;
  amount_nano: string;
  /** Set on `confirmed`; lets us drop the bubble once the real one arrives. */
  tx_hash: string | null;
  status: PendingStatus;
  /** epoch ms — used to surface the "tap if confirmed" reconcile prompt. */
  created_at: number;
}

type Listener = () => void;

class PendingStore {
  private map = new Map<string, PendingMessage>();
  private listeners = new Set<Listener>();
  /**
   * Cached snapshot arrays per counterparty key. `useSyncExternalStore`
   * requires the getSnapshot return value to be referentially stable when
   * nothing has changed, otherwise React tears it down and rebuilds — which
   * also blows up the "no infinite renders" invariant in StrictMode.
   *
   * We invalidate the cache on every mutation; cheaper than computing diffs.
   */
  private snapshotCache = new Map<string, PendingMessage[]>();

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  private emit(): void {
    this.snapshotCache.clear();
    for (const l of this.listeners) l();
  }

  getAll = (): PendingMessage[] => {
    return Array.from(this.map.values());
  };

  getForCounterparty(counterpartyRaw: string): PendingMessage[] {
    const cached = this.snapshotCache.get(counterpartyRaw);
    if (cached !== undefined) return cached;
    const out: PendingMessage[] = [];
    for (const p of this.map.values()) {
      if (p.counterparty_raw === counterpartyRaw) out.push(p);
    }
    out.sort((a, b) => a.created_at - b.created_at);
    // Reuse EMPTY for the common "no pending" case so React sees referential
    // stability and skips the rerender.
    const stable = out.length === 0 ? EMPTY : out;
    this.snapshotCache.set(counterpartyRaw, stable);
    return stable;
  }

  add(p: Omit<PendingMessage, "created_at" | "status" | "tx_hash"> & {
    status?: PendingStatus;
  }): PendingMessage {
    const row: PendingMessage = {
      ...p,
      tx_hash: null,
      status: p.status ?? "signing",
      created_at: Date.now(),
    };
    this.map.set(p.pending_id, row);
    this.emit();
    return row;
  }

  setStatus(pendingId: string, status: PendingStatus, tx_hash?: string): void {
    const existing = this.map.get(pendingId);
    if (!existing) return;
    this.map.set(pendingId, {
      ...existing,
      status,
      tx_hash: tx_hash ?? existing.tx_hash,
    });
    this.emit();
  }

  remove(pendingId: string): void {
    if (this.map.delete(pendingId)) this.emit();
  }

  /**
   * Drop any pending whose `tx_hash` is now present in the given thread
   * message list. Called when the thread query refetches with the real row.
   */
  reconcileWithThread(messages: ThreadMessage[]): void {
    if (this.map.size === 0) return;
    let changed = false;
    const hashes = new Set(messages.map((m) => m.tx_hash));
    for (const [id, p] of this.map) {
      if (p.tx_hash !== null && hashes.has(p.tx_hash)) {
        this.map.delete(id);
        changed = true;
      }
    }
    if (changed) this.emit();
  }

  /**
   * Client-side fallback when server reconciler can't match (whitespace,
   * encoding, etc.). When a confirmed outbound message arrives via SSE,
   * drop any pending bubble that matches (counterparty, amount, comment).
   * Comment matched with trim() so trailing whitespace doesn't block it.
   */
  reconcileByContent(args: {
    counterparty_raw: string;
    amount_nano: string;
    comment: string;
    tx_hash: string;
  }): void {
    if (this.map.size === 0) return;
    const wantComment = args.comment.trim();
    let changed = false;
    for (const [id, p] of this.map) {
      if (
        p.counterparty_raw === args.counterparty_raw &&
        p.amount_nano === args.amount_nano &&
        p.comment.trim() === wantComment
      ) {
        this.map.delete(id);
        changed = true;
      }
    }
    if (changed) this.emit();
  }
}

const EMPTY: PendingMessage[] = [];

export const pendingStore = new PendingStore();

/** Hook: subscribe to pending messages for one thread. */
export function usePendingThread(counterpartyRaw: string | null): PendingMessage[] {
  return useSyncExternalStore(
    pendingStore.subscribe,
    () => {
      if (counterpartyRaw === null) return EMPTY;
      return pendingStore.getForCounterparty(counterpartyRaw);
    },
    () => EMPTY,
  );
}
